import { useEffect, useState } from "react";
import { Link, useParams, useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { Gift, Check, ArrowRight } from "lucide-react";
import { api } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";

export default function GiftClaim() {
  const { code } = useParams();
  const navigate = useNavigate();
  const { user } = useAuth();
  const authed = !!user && user !== false;
  const [gift, setGift] = useState(undefined);
  const [busy, setBusy] = useState(false);
  const [opened, setOpened] = useState(false);

  useEffect(() => {
    api.get(`/gifts/${code}`).then((r) => setGift(r.data)).catch(() => setGift(null));
  }, [code]);

  const claim = async () => {
    if (!authed) { navigate(`/connexion?next=/cadeau/${code}`); return; }
    setBusy(true);
    try {
      await api.post(`/gifts/${code}/claim`);
      setOpened(true);
      toast.success("Votre coffret est ouvert !");
    } catch (e) { toast.error(e.response?.data?.detail || "Ouverture impossible"); }
    setBusy(false);
  };

  if (gift === undefined) return <p className="max-w-xl mx-auto px-4 py-24 font-sans text-[#1F2A24]/60">Chargement…</p>;
  if (gift === null) return (
    <div className="max-w-md mx-auto px-4 py-28 text-center" data-testid="gift-not-found">
      <Gift className="mx-auto text-[#C9A24B]" size={30} />
      <p className="font-serif text-2xl text-[#1F2A24] mt-3">Cadeau introuvable</p>
      <p className="font-sans text-sm text-[#1F2A24]/60 mt-2">Vérifiez le lien reçu par e-mail.</p>
    </div>
  );

  return (
    <div className="max-w-2xl mx-auto px-4 sm:px-8 py-14 md:py-20" data-testid="gift-page">
      <div className="rounded-3xl bg-[#1F2A24] text-[#F6F2E9] p-8 md:p-12 animate-in fade-in slide-in-from-bottom-4 duration-700">
        <span className="inline-flex items-center gap-2 rounded-full bg-[#C9A24B] text-[#1F2A24] px-4 py-1.5 font-ui text-xs tracking-[0.2em] uppercase font-semibold"><Gift size={14} /> Un cadeau pour vous</span>
        <h1 className="font-serif text-3xl sm:text-4xl mt-5" data-testid="gift-title">{gift.sender_name} vous offre le Coffret Découverte du Tao des 4C</h1>
        {gift.message && (
          <blockquote className="mt-6 border-l-2 border-[#C9A24B] pl-5 font-serif italic text-lg text-[#F6F2E9]/85 whitespace-pre-line" data-testid="gift-message">
            {gift.message}
            <span className="block not-italic font-sans text-sm text-[#F6F2E9]/50 mt-2">— {gift.sender_name}</span>
          </blockquote>
        )}
        <ul className="mt-7 space-y-2 font-sans text-sm" data-testid="gift-contents">
          {gift.contents.map((c) => <li key={c} className="flex gap-2"><Check size={16} className="text-[#C9A24B] shrink-0" /> {c}</li>)}
        </ul>
        {opened || (gift.claimed && authed) ? (
          <Link to="/mon-espace" data-testid="gift-go-space" className="mt-8 inline-flex items-center gap-2 rounded-full bg-[#C9A24B] text-[#1F2A24] px-7 py-3.5 font-ui text-sm hover:bg-[#b8923f]">Découvrir mon espace <ArrowRight size={16} /></Link>
        ) : gift.claimed ? (
          <p className="mt-8 font-sans text-sm text-[#F6F2E9]/70" data-testid="gift-claimed">Ce cadeau a déjà été ouvert. Connectez-vous pour retrouver vos contenus dans Mon espace.</p>
        ) : (
          <button onClick={claim} disabled={busy} data-testid="gift-claim-btn" className="mt-8 inline-flex items-center gap-2 rounded-full bg-[#C9A24B] text-[#1F2A24] px-7 py-3.5 font-ui text-sm hover:bg-[#b8923f] disabled:opacity-60">
            {busy ? "Ouverture…" : authed ? "Ouvrir mon cadeau" : "Me connecter pour ouvrir mon cadeau"} <ArrowRight size={16} />
          </button>
        )}
      </div>
    </div>
  );
}
